import React, { useEffect, useMemo, useState } from 'react';
import { ratiosAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Loader2, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';

const GREEN_MAX = 32;
const RED_MIN = 40;

function ratioClass(r) {
    if (r == null) return 'text-slate-300';
    if (r <= GREEN_MAX) return 'text-emerald-600 font-semibold';
    if (r >= RED_MIN) return 'text-red-600 font-bold';
    return 'text-amber-600 font-semibold';
}

function Ratio({ value }) {
    return <span className={ratioClass(value)}>{value == null ? '—' : `${value.toFixed(1)} %`}</span>;
}

const todayMonth = () => new Date().toISOString().slice(0, 7);
const mondayOfToday = () => {
    const d = new Date();
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d.toISOString().slice(0, 10);
};

export default function MatiereTab() {
    const [mode, setMode] = useState('month');
    const [month, setMonth] = useState(todayMonth());
    const [weekStart, setWeekStart] = useState(mondayOfToday());
    const [net, setNet] = useState(true);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = mode === 'month'
                ? await ratiosAPI.month({ month, history: 6 })
                : await ratiosAPI.week({ start: weekStart });
            setData(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, month, weekStart]);

    const rk = net ? 'ratio_net' : 'ratio_gross';
    const ck = net ? 'ca_net' : 'ca_gross';
    const rows = data?.rows || [];
    const totals = data?.totals;
    const hasTransfers = useMemo(() => rows.some(r => r.transfers_in > 0 || r.transfers_out > 0), [rows]);

    return (
        <div className="space-y-4" data-testid="matiere-tab">
            <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-lg border overflow-hidden">
                    <button type="button" onClick={() => setMode('month')} data-testid="matiere-mode-month"
                        className={`px-3 h-8 text-xs font-semibold ${mode === 'month' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}>Mois</button>
                    <button type="button" onClick={() => setMode('week')} data-testid="matiere-mode-week"
                        className={`px-3 h-8 text-xs font-semibold ${mode === 'week' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}>Semaine</button>
                </div>
                {mode === 'month' ? (
                    <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                        className="h-8 px-2 rounded-lg border text-xs" data-testid="matiere-month-input" />
                ) : (
                    <input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)}
                        className="h-8 px-2 rounded-lg border text-xs" data-testid="matiere-week-input" />
                )}
                <div className="inline-flex rounded-lg border overflow-hidden">
                    <button type="button" onClick={() => setNet(true)} data-testid="matiere-ca-net"
                        title={`CA net de commission (−${data?.commission_pct ?? 15} %)`}
                        className={`px-3 h-8 text-xs font-semibold ${net ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}>CA net</button>
                    <button type="button" onClick={() => setNet(false)} data-testid="matiere-ca-gross"
                        className={`px-3 h-8 text-xs font-semibold ${!net ? 'bg-slate-900 text-white' : 'bg-white text-slate-600'}`}>CA brut</button>
                </div>
                <span className="text-[11px] text-slate-400 flex items-center gap-1">
                    <Info className="w-3.5 h-3.5" />
                    Ratio d&rsquo;achats = achats réceptionnés ± transferts / CA (approximation sans inventaires)
                </span>
            </div>

            {mode === 'week' && (
                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="matiere-week-warning">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    Vue hebdomadaire indicative : sans inventaires, une grosse livraison gonfle la semaine (le stock résiduel n&rsquo;est pas déduit). Le mois est plus fiable.
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : (
                <>
                    <div className="border rounded-lg overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 text-slate-500">
                                <tr>
                                    <th className="text-left px-3 py-2">Site</th>
                                    <th className="text-right px-3 py-2">Réceptions</th>
                                    <th className="text-right px-3 py-2">Transferts +</th>
                                    <th className="text-right px-3 py-2">Transferts −</th>
                                    <th className="text-right px-3 py-2">Total achats</th>
                                    <th className="text-right px-3 py-2">{net ? 'CA net' : 'CA brut'}</th>
                                    <th className="text-right px-3 py-2">Ratio</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.site_id} className="border-t" data-testid={`matiere-row-${r.site_id}`}>
                                        <td className="px-3 py-1.5 font-medium">{r.site_name}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(r.purchases)}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums text-violet-600">{r.transfers_in ? `+${formatCurrency(r.transfers_in)}` : '—'}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums text-violet-600">{r.transfers_out ? `−${formatCurrency(r.transfers_out)}` : '—'}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{formatCurrency(r.achats)}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(r[ck])}</td>
                                        <td className="px-3 py-1.5 text-right tabular-nums" data-testid={`matiere-ratio-${r.site_id}`}><Ratio value={r[rk]} /></td>
                                    </tr>
                                ))}
                            </tbody>
                            {totals && (
                                <tfoot>
                                    <tr className="border-t bg-slate-900 text-white font-semibold">
                                        <td className="px-3 py-2">Total Fuji Express</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(totals.purchases)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{totals.transfers_in ? `+${formatCurrency(totals.transfers_in)}` : '—'}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{totals.transfers_out ? `−${formatCurrency(totals.transfers_out)}` : '—'}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(totals.achats)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(totals[ck])}</td>
                                        <td className="px-3 py-2 text-right tabular-nums" data-testid="matiere-ratio-total">
                                            {totals[rk] == null ? '—' : `${totals[rk].toFixed(1)} %`}
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>

                    {!hasTransfers && mode === 'month' && (
                        <p className="text-[11px] text-slate-400">Aucun transfert inter-sites validé sur ce mois.</p>
                    )}

                    {mode === 'month' && data?.history_months && (
                        <div className="space-y-1">
                            <h3 className="text-sm font-semibold text-slate-700">Historique 6 mois — ratio {net ? 'net' : 'brut'}</h3>
                            <div className="border rounded-lg overflow-x-auto">
                                <table className="w-full text-xs" data-testid="matiere-history-table">
                                    <thead className="bg-slate-50 text-slate-500">
                                        <tr>
                                            <th className="text-left px-3 py-2">Site</th>
                                            {data.history_months.map(m => (
                                                <th key={m} className="text-right px-3 py-2">{m.slice(5, 7)}/{m.slice(2, 4)}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.filter(r => r.site_name !== 'FE Siège').map(r => (
                                            <tr key={r.site_id} className="border-t">
                                                <td className="px-3 py-1.5 font-medium">{r.site_name}</td>
                                                {data.history_months.map(m => (
                                                    <td key={m} className="px-3 py-1.5 text-right tabular-nums">
                                                        <Ratio value={data.history[m]?.sites?.[r.site_id]?.[rk]} />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                        <tr className="border-t bg-slate-50 font-semibold">
                                            <td className="px-3 py-1.5">Total FE</td>
                                            {data.history_months.map(m => (
                                                <td key={m} className="px-3 py-1.5 text-right tabular-nums">
                                                    <Ratio value={data.history[m]?.total?.[rk]} />
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-[11px] text-slate-400">
                                Code couleur : <span className="text-emerald-600 font-semibold">≤ {GREEN_MAX} %</span> ·{' '}
                                <span className="text-amber-600 font-semibold">{GREEN_MAX}–{RED_MIN} %</span> ·{' '}
                                <span className="text-red-600 font-semibold">≥ {RED_MIN} %</span>
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
